import type { PageEntry, Node } from 'contensis-delivery-api';
import dynamic from 'next/dynamic';
import { useTheme } from 'styled-components';

import cmsParser from 'Common/util/cmsParser';
import { FiftyTwoWeeksArticlePage } from 'Common/util/cmsCanvas';

import type { Props } from './Article';
import { FiftyTwoWeeksCardProps } from 'Components/Card/FiftyTwoWeeks';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
    signUpPromo?: Node;
    prevWeek?: Node;
    nextWeek?: Node;
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors, signUpPromo, prevWeek, nextWeek } = data;

    const {
        title,
        sys,
        canvas,
        articleTitle,
        articleSummary,
        weekNumber,
        articleMainHeaderImage,
        articleMainVideo
    } = entry;

    const { colors } = useTheme();

    const fiftyTwoWeeksCardData = (
        weekStatus: { status: string; colour: string },
        color: string,
        data?: Node
    ): FiftyTwoWeeksCardProps => {
        return {
            heading: data?.articleTitle,
            image: data?.articleMainHeaderImage && cmsParser.image(data.articleMainHeaderImage),
            calendarColour: data && color,
            href: data?.sys.uri,
            weekStatus: weekStatus,
            week: data?.weekNumber,
            description: data?.articleSummary
        };
    };

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        canvasContent: canvas && canvas.length > 0 ? FiftyTwoWeeksArticlePage(canvas) : undefined,
        signUpPromo: {
            icon: dynamic(() =>
                import('@solent-university/solent-icons').then(
                    icons => icons[signUpPromo.icon.name]
                )
            ),
            heading: signUpPromo.heading,
            text: signUpPromo.description,
            link: signUpPromo.button?.link && {
                ...cmsParser.linkButton(signUpPromo.button),
                variant: 'accentTwoTextWhiteBg'
            },
            backgroundColour: colors.accents[2].hex
        },
        week: { week: weekNumber, color: colors.primary.hex },
        heading: articleTitle,
        summary: articleSummary,
        video: articleMainVideo && cmsParser.transcriptEmbed(articleMainVideo),
        image: cmsParser.image(articleMainHeaderImage),
        prevCard: fiftyTwoWeeksCardData(
            { status: 'Previous week', colour: '#197A6E' },
            colors.accents[7].hex,
            prevWeek
        ),
        nextCard: fiftyTwoWeeksCardData(
            { status: 'Upcoming week', colour: '#7536C3' },
            colors.accents[2].hex,
            nextWeek
        )
    };
};

export default datamap;
